// src/pages/business/commission/CommissionTasks.tsx
// Web adaptation of mobile CommissionTasksScreen - Manage commissionable tasks

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Loader2, Search, DollarSign,
  AlertCircle, CheckCircle, XCircle
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

interface CommissionTask {
  id: string;
  task_name: string;
  commission_amount: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const CommissionTasks: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const [tasks, setTasks] = useState<CommissionTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [taskName, setTaskName] = useState('');
  const [taskAmount, setTaskAmount] = useState('');
  const [creating, setCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchTasks();
  }, [businessId]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get(`/businesses/${businessId}/commission-tasks`);
      setTasks(data.tasks || []);
    } catch (error: any) {
      console.error('Error fetching commission tasks:', error);
      toastError('Failed to load commission tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async () => {
    if (!taskName.trim() || !taskAmount.trim()) {
      toastError('Please provide both a task name and commission amount');
      return;
    }

    setCreating(true);
    try {
      await apiClient.post(`/businesses/${businessId}/commission-tasks`, {
        task_name: taskName.trim(),
        commission_amount: parseFloat(taskAmount),
      });
      toastSuccess('Commission task added successfully');
      setTaskName('');
      setTaskAmount('');
      fetchTasks();
    } catch (error: any) {
      toastError('Failed to add commission task');
    } finally {
      setCreating(false);
    }
  };

  const handleRemoveTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to remove this commission task?')) return;

    try {
      await apiClient.delete(`/businesses/${businessId}/commission-tasks/${taskId}`);
      toastSuccess('Commission task removed');
      fetchTasks();
    } catch (error: any) {
      toastError('Failed to remove commission task');
    }
  };

  const toggleTaskStatus = async (taskId: string, currentStatus: boolean) => {
    try {
      await apiClient.patch(`/businesses/${businessId}/commission-tasks/${taskId}`, {
        is_active: !currentStatus,
      });
      fetchTasks();
      toastSuccess(`Task ${currentStatus ? 'disabled' : 'enabled'}`);
    } catch (error: any) {
      toastError('Failed to update task');
    }
  };

  const filteredTasks = tasks.filter((task) =>
    task.task_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
            <ArrowLeft size={24} className="text-primary" />
          </button>
          <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
            Commissionable Tasks
          </h1>
        </div>
        <div className="flex items-center justify-center py-24">
          <Loader2 size={40} className="animate-spin mx-auto text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
          <ArrowLeft size={24} className="text-primary" />
        </button>
        <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
          Commissionable Tasks
        </h1>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Add New Task Card */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm border border-light-border dark:border-dark-border">
          <h2 className="font-semibold text-lg mb-4 text-light-text dark:text-dark-text">Add New Task</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Task Name
              </label>
              <input
                type="text"
                placeholder="e.g., Manicure, Hair Treatment, etc."
                value={taskName}
                onChange={(e) => setTaskName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Commission Amount (KES)
              </label>
              <input
                type="number"
                placeholder="e.g., 500"
                value={taskAmount}
                onChange={(e) => setTaskAmount(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <button
              onClick={handleAddTask}
              disabled={creating || !taskName.trim() || !taskAmount.trim()}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-semibold transition-all hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#4F46E5' }}
            >
              {creating ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus size={16} />
                  Add Task
                </>
              )}
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>

        {/* Tasks List */}
        {filteredTasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
            <DollarSign size={64} className="text-light-subtle dark:text-dark-subtle" />
            <p className="font-semibold text-light-text dark:text-dark-text" style={{ fontSize: 18 }}>
              No Commission Tasks
            </p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle">
              {searchQuery ? 'Try adjusting your search' : 'Add commission tasks to track staff commissions'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTasks.map((task) => (
              <div
                key={task.id}
                className="flex items-center justify-between p-4 bg-light-card dark:bg-dark-card rounded-xl border border-light-border dark:border-dark-border shadow-sm"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-light-text dark:text-dark-text">{task.task_name}</p>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        task.is_active
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                          : 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {task.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <p className="font-bold text-lg" style={{ color: '#4F46E5' }}>
                    KES {task.commission_amount.toLocaleString()}
                  </p>
                  <button
                    onClick={() => toggleTaskStatus(task.id, task.is_active)}
                    className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                  >
                    {task.is_active ? (
                      <XCircle size={16} className="text-light-subtle dark:text-dark-subtle" />
                    ) : (
                      <CheckCircle size={16} className="text-green-500" />
                    )}
                  </button>
                  <button
                    onClick={() => handleRemoveTask(task.id)}
                    className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionTasks;